import { useCallback, useRef, useState } from 'react';
import {
  faImage,
  faLink,
  faPlus,
  faSend,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  MarkdownEditor,
  MarkdownEditorHandle,
} from '@design-system/components/markdownEditor';
import { Button } from '@design-system/components/button/button';
import { useNewPost } from '../lib/useNewPost';
import {
  communityMentionConfig,
  containsCommunityMention,
} from '../lib/channelMention';
import { CreatorIdentity } from '../api/useCommunityCreatorIdentity';
import { CommunityId } from '../model/community.types';
import { ACCEPTED_MEDIA_TYPES } from '../model/newPost.constants';
import { MentionConfirmModal } from './MentionConfirmModal';
import { NewPostMediaPreview } from './NewPostMediaPreview';

type Props = {
  communityId: CommunityId;
  author: CreatorIdentity | null;
  influencerId?: string | number;
  memberCount?: number;
};

const draggingFiles = (event: React.DragEvent) =>
  event.dataTransfer.types.includes('Files');

const CreatorAvatar = ({ author }: { author: CreatorIdentity | null }) => {
  if (author?.avatarUrl) {
    return (
      <img
        src={author.avatarUrl}
        alt={author.name}
        className="size-10 shrink-0 rounded-full object-cover"
      />
    );
  }

  return (
    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#CC1EB8] text-base font-bold text-white">
      {(author?.name.charAt(0) ?? '').toUpperCase()}
    </span>
  );
};

export const NewPostField = ({
  communityId,
  author,
  influencerId,
  memberCount,
}: Props) => {
  const {
    text,
    setText,
    media,
    addMedia,
    removeMedia,
    canSubmit,
    isSubmitting,
    submit,
  } = useNewPost(communityId, influencerId);
  const editorRef = useRef<MarkdownEditorHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [showMentionConfirm, setShowMentionConfirm] = useState(false);
  const dragDepth = useRef(0);

  const requestSubmit = () => {
    if (containsCommunityMention(text)) {
      setShowMentionConfirm(true);
      return;
    }
    void submit();
  };

  const confirmMentionSubmit = () => {
    setShowMentionConfirm(false);
    void submit();
  };

  const onFilesSelected = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files ? Array.from(event.target.files) : [];
      if (files.length > 0) addMedia(files);
      event.target.value = '';
    },
    [addMedia],
  );

  const onDragEnter = useCallback(
    (event: React.DragEvent) => {
      if (isSubmitting || !draggingFiles(event)) return;
      event.preventDefault();
      dragDepth.current += 1;
      setIsDraggingFiles(true);
    },
    [isSubmitting],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    if (!draggingFiles(event)) return;
    event.preventDefault();
  }, []);

  const onDragLeave = useCallback((event: React.DragEvent) => {
    if (!draggingFiles(event)) return;
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setIsDraggingFiles(false);
    }
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      dragDepth.current = 0;
      setIsDraggingFiles(false);
      if (isSubmitting) return;
      const dropped = Array.from(event.dataTransfer.files).filter((file) =>
        ACCEPTED_MEDIA_TYPES.some((type) => type === file.type),
      );
      if (dropped.length > 0) addMedia(dropped);
    },
    [addMedia, isSubmitting],
  );

  return (
    <div
      className="relative flex flex-col gap-4 rounded-[10px] bg-white p-4"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {isDraggingFiles && (
        <div
          data-testid="new-post-dropzone"
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[10px] border-2 border-dashed border-primary-800 bg-primary-800/10"
        >
          <FontAwesomeIcon
            icon={faPlus}
            className="text-4xl text-primary-800"
          />
        </div>
      )}
      <div className="flex gap-2.5">
        <CreatorAvatar author={author} />
        <div className="min-w-0 flex-1">
          <MarkdownEditor
            ref={editorRef}
            value={text}
            onChange={setText}
            placeholder="Tell us something..."
            disabled={isSubmitting}
            mention={communityMentionConfig}
          />
        </div>
      </div>

      <NewPostMediaPreview media={media} onRemove={removeMedia} />

      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button
            type="button"
            aria-label="Add media"
            disabled={isSubmitting}
            onClick={() => fileInputRef.current?.click()}
            className="flex size-[42px] items-center justify-center rounded-full bg-primary-150 text-xs text-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faImage} />
          </button>
          <button
            type="button"
            aria-label="Add link"
            disabled={isSubmitting}
            onClick={() => editorRef.current?.openAddLink()}
            className="flex size-[42px] items-center justify-center rounded-full bg-primary-150 text-xs text-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faLink} />
          </button>
          <input
            type="file"
            accept={ACCEPTED_MEDIA_TYPES.join(', ')}
            ref={fileInputRef}
            onChange={onFilesSelected}
            hidden
            multiple
          />
        </div>
        <Button
          color="primary"
          leftIcon={faSend}
          loading={isSubmitting}
          disabled={!canSubmit}
          onClick={requestSubmit}
        >
          Post
        </Button>
      </div>

      <MentionConfirmModal
        open={showMentionConfirm}
        memberCount={memberCount}
        onConfirm={confirmMentionSubmit}
        onCancel={() => setShowMentionConfirm(false)}
      />
    </div>
  );
};
